import { Skeleton } from "antd"
import { motion } from "motion/react"
import React, { Suspense, useState } from "react"

import { type RoleKeys } from "../../../../constants/roles.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import type { ProjectSectionKeys } from "./ProjectViewSectionsTabs.tsx"
import { FinancialAlertsPanel } from "./sections/ProjectViewFinance/FinancialAlertsPanel.tsx"
import { ProjectViewFinanceKpi } from "./sections/ProjectViewFinance/ProjectViewFinanceKpi.tsx"
import { ProjectViewInvoiceSnapshot } from "./sections/ProjectViewInvoices/ProjectViewInvoiceSnapshot.tsx"
import { ProjectViewIndicators } from "./sections/ProjectViewMilestones/ProjectViewIndicators.tsx"

const TABS_WITH_SNAPSHOT: ProjectSectionKeys[] = [
  "milestones",
  "finance",
  "invoices",
  "tasks",
  "commissions"
]

interface ProjectViewSidebarProps {
  activeTab: ProjectSectionKeys | null
  //
  showSalesSnapshot: boolean
  showCommissionSnapshot: boolean
  showInvoiceSnapshot: boolean
  showMilestoneSnapshot: boolean
}

export function ProjectViewSidebar({
  activeTab,
  showCommissionSnapshot,
  showSalesSnapshot,
  showInvoiceSnapshot,
  showMilestoneSnapshot
}: ProjectViewSidebarProps) {
  const collapsed = useAppSelector((state) => state?.project?.showSnapshot)

  const hasContent =
    activeTab !== null && TABS_WITH_SNAPSHOT.includes(activeTab)
  const isVisible = hasContent && !collapsed

  // Hidden rather than unmounted: every widget fetches on mount.
  const [hasBeenVisible, setHasBeenVisible] = useState(isVisible)
  const [fadedOut, setFadedOut] = useState(false)
  if (isVisible && !hasBeenVisible) setHasBeenVisible(true)
  if (isVisible && fadedOut) setFadedOut(false)

  return (
    <motion.div
      initial={false}
      animate={{
        width: isVisible ? 565 : 0,
        opacity: isVisible ? 1 : 0
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`max-xl:hidden ${isVisible ? "ml-3" : ""}`}
    >
      {hasBeenVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={
            isVisible
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: -10, transitionEnd: { y: 10 } }
          }
          transition={{ duration: 0.2 }}
          onAnimationComplete={() => setFadedOut(!isVisible)}
          hidden={fadedOut}
          className="w-full flex overflow-auto tailwind-scroll flex-col gap-3 size-full"
        >
          <Suspense
            fallback={<Skeleton.Node active className="size-full flex-1" />}
          >
            {showMilestoneSnapshot && <ProjectViewIndicators />}
            {(showSalesSnapshot || showCommissionSnapshot) && (
              <>
                <ProjectViewFinanceKpi
                  showSalesSnapshot={showSalesSnapshot}
                  showCommissionSnapshot={showCommissionSnapshot}
                />
                <FinancialAlertsPanel />
              </>
            )}
            {showInvoiceSnapshot && <ProjectViewInvoiceSnapshot />}
          </Suspense>
        </motion.div>
      )}
    </motion.div>
  )
}
